import React, { useState } from "react";
import { Button, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/styles";
import { Calendar as CalendarIcon, ListDashes } from "@phosphor-icons/react";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

import { TIMEZONES } from "@constants";
import { COLOR_CHIPS, ANCalendar } from "@answersnow/pkg-theme";
import { formatTimeZoneToAbbreviation } from "utils/formatTimeZoneToAbbreviation";
import { LoadingScreen, UserTitle } from "../EventSchedulingUtils";
import useStyles from "./styles"; // Adjust the import path as needed
/** @typedef {import("utils/ColorIndexManager").default} ColorIndexManager */

/**
 * Component to view availability and schedule for clients and clinicians.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.values - Form values including the event date and client information.
 * @param {Array} props.selectedClinicians - List of selected clinicians.
 * @param {Function} props.setFieldValue - Formik's setFieldValue function to set form values.
 * @param {Array} props.allEvents - List of all events to display on the calendar.
 * @param {Array} props.nonWorkingHours - List of non-working hours to display on the calendar.
 * @param {Object} props.mergedTimeSlots - Merged time slots for clinicians and clients.
 * @param {boolean} props.loadingClinicianAvailability - Loading state for clinician availability.
 * @param {Array} props.clientAvailability - List of client's available time slots.
 * @param {ColorIndexManager} props.colorManager - Color manager for the calendar.
 * @param {Function} props.onChangeBuiltEvent - Function to call when a built event is changed.
 * @returns {React.ReactElement} - The rendered AvailabilityViewer component.
 */
const AvailabilityViewer = ({
  values,
  selectedClinicians,
  setFieldValue,
  allEvents,
  nonWorkingHours,
  mergedTimeSlots,
  loadingClinicianAvailability,
  clientAvailability,
  colorManager,
  onChangeBuiltEvent,
}) => {
  const clientLastName =
    values.selectedClient?.child_last_name ||
    (values.selectedClient && values.selectedClient.teamMembers
      ? values.selectedTeamMember?.lastName
      : "");
  const clientFirstName = values.selectedClient?.name ?? "";
  const theme = useTheme();
  const classes = useStyles();
  const [toggleButtonGroupValue, setToggleButtonGroupValue] = useState("schedule");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState({ clinicianId: null, index: null });

  // Get local timezone and abbreviation
  const timezoneIANA = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezone = TIMEZONES[timezoneIANA] || timezoneIANA;
  const timezoneAbbr = formatTimeZoneToAbbreviation(timezone, new Date(values.eventDate));

  // Get selected team member
  const selectedClient = values.selectedClient;

  return (
    <Stack flexGrow={1}>
      <Stack sx={{ marginRight: theme.spacing(5) }} alignItems="flex-end">
        <ToggleButtonGroup
          value={toggleButtonGroupValue}
          exclusive
          onChange={(_, newVal) => {
            if (newVal) setToggleButtonGroupValue(newVal);
          }}
          aria-label="Schedule / Availability"
          size="small"
        >
          <Tooltip title="Schedule">
            <ToggleButton value="schedule" aria-label="schedule">
              <CalendarIcon />
            </ToggleButton>
          </Tooltip>
          <Tooltip title="Availability">
            <ToggleButton value="availability" aria-label="availability">
              <ListDashes />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Stack>

      <Stack
        direction="row"
        gap={theme.spacing(1)}
        padding={theme.spacing(5)}
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="body2">{format(new Date(values.eventDate), "eee")}</Typography>
        <Typography variant="subtitle2" className={classes.selectedDate}>
          {format(new Date(values.eventDate), "d")}
        </Typography>
      </Stack>

      <Typography
        variant="caption"
        align="center"
        color="textSecondary"
        sx={{ marginBottom: theme.spacing(2), fontStyle: "italic" }}
      >
        All times are shown in {timezone} ({timezoneAbbr})
      </Typography>

      {toggleButtonGroupValue === "schedule" && (
        <>
          <Stack direction="row" height="20px" justifyContent="center">
            {selectedClinicians &&
              selectedClinicians?.length > 0 &&
              selectedClinicians.map((clinician) => (
                <UserTitle
                  key={clinician.clinician_id}
                  dotColor={COLOR_CHIPS[colorManager.get(clinician.clinician_id)].color}
                  name={`${clinician?.first_name} ${clinician?.last_name}`}
                />
              ))}

            {values.selectedClient && (
              <UserTitle
                dotColor={COLOR_CHIPS[colorManager.get(selectedClient.clientId)].color}
                name={`${clientFirstName}`}
              />
            )}
          </Stack>

          <ANCalendar
            dragAndDrop
            draggableAccessor={(event) => event.call_type === "BUILDING"}
            onChangeEvent={onChangeBuiltEvent}
            className={classes.calendarTheme}
            view="day"
            onView={() => {}}
            onNavigate={() => {}}
            events={allEvents}
            date={values.eventDate}
            nonWorkingHours={nonWorkingHours}
            toolbar={false}
            style={{ height: 0, flexGrow: 1 }}
            colorIndexManager={colorManager}
            scrollToTime={new Date(0, 0, 0, 8)}
          />
        </>
      )}
      {toggleButtonGroupValue === "availability" && (
        <Stack gap={theme.spacing(3)} padding={theme.spacing(0, 5)} overflow="auto">
          {values?.selectedClient?.name && (
            <React.Fragment>
              <Typography variant="subtitle1">
                {`${`${clientFirstName} ${clientLastName}`}`} Availability
              </Typography>
              {clientAvailability?.map(({ start }, index) => (
                <Button
                  color={
                    selectedTimeSlot.clinicianId === "client" && selectedTimeSlot.index === index
                      ? "primary"
                      : "secondary"
                  }
                  key={index}
                  onClick={() => {
                    setSelectedTimeSlot({ clinicianId: "client", index });
                    setFieldValue(
                      "startTime",
                      formatInTimeZone(new Date(start), timezoneIANA, "h:mm a"),
                      true
                    );
                  }}
                >
                  {formatInTimeZone(new Date(start), timezoneIANA, "h:mm a")}
                </Button>
              ))}
              {(!clientAvailability || clientAvailability?.length === 0) && (
                <Typography variant="body2" color={(theme) => theme.palette.text.secondary}>
                  {`No available times for ${clientFirstName} ${clientLastName} on this day.`}
                </Typography>
              )}
            </React.Fragment>
          )}
          {selectedClinicians &&
            selectedClinicians?.length > 0 &&
            selectedClinicians.map((clinician) => (
              <React.Fragment key={clinician.clinician_id}>
                <Typography
                  variant="subtitle1"
                  paddingTop={"16px"}
                >{`${clinician.first_name} ${clinician.last_name}`}</Typography>
                {mergedTimeSlots[clinician.clinician_user_id]?.map((time, index) => (
                  <Button
                    color={
                      selectedTimeSlot.clinicianId === clinician.clinician_id &&
                      selectedTimeSlot.index === index
                        ? "primary"
                        : "secondary"
                    }
                    key={index}
                    onClick={() => {
                      setSelectedTimeSlot({ clinicianId: clinician.clinician_id, index });
                      setFieldValue(
                        "startTime",
                        formatInTimeZone(new Date(time), timezoneIANA, "h:mm a"),
                        true
                      );
                    }}
                  >
                    {formatInTimeZone(new Date(time), timezoneIANA, "h:mm a")}
                  </Button>
                ))}
                {(!mergedTimeSlots[clinician.clinician_user_id] ||
                  mergedTimeSlots[clinician.clinician_user_id]?.length === 0) && (
                  <Typography variant="body2" color={(theme) => theme.palette.text.secondary}>
                    No available times for {clinician.first_name} {clinician.last_name} on this day.
                  </Typography>
                )}
              </React.Fragment>
            ))}
          {loadingClinicianAvailability ? (
            <LoadingScreen />
          ) : (
            selectedClinicians?.length === 0 && (
              <Typography variant="body2" color={(theme) => theme.palette.text.secondary}>
                No available times on this day.
              </Typography>
            )
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default AvailabilityViewer;
